import {
	AuthEvents,
	AuthState,
	AuthStorageKeys,
	clearUserInfo,
	verificationConstants,
} from '../../config/auth-enumerations'
import {
	getRegionRedirect,
	getTimeToExpiration,
	getVerificationStatusFromToken,
	isTokenExpired,
	parseTokenForExpiration,
	redirectToVerificationPage,
	validateToken,
} from './verify-token'
import { ClientApplication } from '../../models/client-application'
import { InactivityEvents, InactivityStorageKeys, InactivityTracker } from '../inactivity-tracker'
import { isAbsoluteUrl } from '@gbds/core/src/ts/utils'
import { requiredMIVal } from '../../config/script-attributes'
import { URLConfig } from '../../components/global-nav/configs/base'
import { dispatchGbpeAuthStateUpdated } from './dispatch-gbpe-auth-state-updated'

/**
 * Custom client application based on sessionStorage (external factors)
 */
export class CustomClientApplication implements ClientApplication {
	protected _authenticated: boolean
	protected _idToken: string | null
	protected _accessToken: string | null
	/** Must start as {@link AuthState.loading} so {@link HeadlessAuth} `loaded` waits for token validation (matches {@link StandardClientApplication}). */
	protected _authState: number = AuthState.loading
	protected _timer: InactivityTracker
	protected _boundStorageHandler: (event: StorageEvent) => void
	protected _boundInactiveUserHandler: () => void

	constructor(private _urlConfig: URLConfig, required = false) {
		this._idToken = this._getExternalIdToken()
		this._accessToken = this._getExternalAccessToken()
		const emailVerifiedProperty = getVerificationStatusFromToken(this._idToken)
		const emailVerified =
			emailVerifiedProperty && emailVerifiedProperty.toLowerCase() === verificationConstants.verified
		//Make sure email is verified so we can send them to verification page first
		if (!emailVerified) {
			this._handleUnverifiedUser()
		}

		//Validate tokens and update authenticated state
		this._validateTokens().then(value => {
			if (value === true) {
				this._setAuthenticated()
				this._setExpireTracker()
			} else {
				this._setUnAuthenticated()
			}
		})
	}

	//Private Methods

	/**
	 * Validates the id token, and if available, the access token
	 * returns a Promise to return a boolean value
	 * true if the present tokens are valid, false otherwise
	 * @private
	 */
	private async _validateTokens(): Promise<boolean> {
		try {
			if (this._idToken) {
				const idTokenValid = await validateToken(this._idToken)
				if (this._accessToken) {
					const accessTokenValid = await validateToken(this._accessToken)
					return idTokenValid && accessTokenValid
				} else {
					console.warn('access token is not present')
					return idTokenValid
				}
			} else {
				console.warn('no id token to validate')
				return false
			}
		} catch (error) {
			console.log(error)
			return false
		}
	}

	/**
	 * Sets the authenticated state of the Headless Auth to authenticated state
	 * Also sets up an inactivity tracker for the Headless Auth User
	 * @private
	 */
	protected _setAuthenticated() {
		this._authenticated = true
		this._authState = AuthState.authenticated
		dispatchGbpeAuthStateUpdated()
		this._dispatchAuthChecked()

		//Here is where we get the tracker for inactivity
		const customTimeout = sessionStorage.getItem(InactivityStorageKeys.customTimeOut)
		//We first need to get the custom timeout in session storage, determine if it's valid
		//and we need to make sure we don't leave a vulnerability by allowing more than default time for inactivity
		const timeLimit =
			customTimeout && !isNaN(parseInt(customTimeout)) && parseInt(customTimeout) <= 15 * 60 * 1000
				? parseInt(customTimeout)
				: 15 * 60 * 1000 // 15 Minutes
		this._timer = new InactivityTracker(timeLimit)

		//Bind and add the inactive user function to the inactive user event listener
		this._boundInactiveUserHandler = this._handleInactiveUser.bind(this)
		window.addEventListener(InactivityEvents.inactiveUser, this._boundInactiveUserHandler)

		//Bind and add the storage handler function to the storage event listener
		this._boundStorageHandler = this._handleStorageEvent.bind(this)
		window.addEventListener('storage', this._boundStorageHandler)
	}

	/**
	 * sets authenticated state to unauthenticated
	 * also logs user out of client application now that token has expired
	 * @private
	 */
	private _setUnAuthenticated() {
		this._authenticated = false
		this._authState = AuthState.unAuthenticated
		dispatchGbpeAuthStateUpdated()
		this._dispatchAuthChecked()
		this.logout()
	}

	/**
	 * dispatched event that the Headless Auth has been loaded
	 * @protected
	 */
	protected _dispatchAuthChecked() {
		window.dispatchEvent(new CustomEvent(AuthEvents.loaded))
	}

	/**
	 * return either a non empty id token from sessionStorage or null
	 * @private
	 */
	private _getExternalIdToken(): string | null {
		if (sessionStorage.getItem(AuthStorageKeys.externalIdToken) !== '') {
			return sessionStorage.getItem(AuthStorageKeys.externalIdToken)
		} else {
			return null
		}
	}

	/**
	 * Return either a non-empty access Token from Storage or null
	 * @private
	 */
	private _getExternalAccessToken(): string | null {
		if (sessionStorage.getItem(AuthStorageKeys.externalAccessToken) !== '') {
			return sessionStorage.getItem(AuthStorageKeys.externalAccessToken)
		} else {
			return null
		}
	}

	/**
	 * Returns true if the token has not expired, false otherwise
	 * @private
	 * @param timeToCheck - date that represents when token expires
	 */
	private async _isTokenUnexpired(timeToCheck: Date): Promise<boolean> {
		return isTokenExpired(timeToCheck)
	}

	/**
	 * Sets up a timeout function to log the user out when the token expires
	 * @private
	 */
	private async _setExpireTracker(): Promise<void> {
		const idTokenDate = parseTokenForExpiration(this._idToken)
		const timeTilExpiration = await getTimeToExpiration(idTokenDate)
		window.setTimeout(() => {
			this.logout()
		}, timeTilExpiration)
	}

	/**
	 * Handles redirecting user to verification page
	 * @private
	 */
	private async _handleUnverifiedUser(): Promise<void> {
		//TODO may need to update this down the line for different regions
		const regionRedirect = await getRegionRedirect(
			this._idToken,
			'en-US',
			'US',
			this._urlConfig.MyAccountProfileURL
		)
		redirectToVerificationPage(regionRedirect)
	}

	protected _handleInactiveUser(): void {
		sessionStorage.setItem(InactivityStorageKeys.userSignedOut, 'true')
		this.logout()
	}

	protected _handleStorageEvent(event: StorageEvent): void {
		if (event.key === AuthStorageKeys.userLogout && event.newValue !== null) {
			this.logout()
		} else {
			if (event.key === AuthStorageKeys.userActive && event.newValue !== null) {
				this._timer.resetTimer()
			}
		}
	}

	private _emitLocalStorageLogout(): void {
		localStorage.setItem(AuthStorageKeys.userLogout, 'true')
		localStorage.removeItem(AuthStorageKeys.userLogout)
	}

	//Public Methods

	/**
	 * Public login method for custom mobile application
	 * @param url - optional parameter that denotes login url
	 * @param extraParams - optional argument that represents extra query parameters to be passed to login url
	 */
	loginRedirect(url?: string, extraParams?: { [key: string]: string }, forceLogin?: boolean): void {
		if (url) {
			if (extraParams) {
				const finalUrl = isAbsoluteUrl(url) ? new URL(url) : new URL(url, window.location.origin)
				//append extra params to the final url query params
				let keyVal: keyof typeof extraParams
				for (keyVal in extraParams) {
					finalUrl.searchParams.append(keyVal, extraParams[keyVal])
				}
				window.location.href = finalUrl.href
			} else {
				window.location.href = url
			}
		} else {
			window.location.reload()
		}
	}

	/**
	 * Public logout method for custom mobile application
	 */
	logout(url?: string): void {
		//First, remove items in sessionStorage that dictates whether to use custom client app
		sessionStorage.removeItem(AuthStorageKeys.externalIdToken)
		sessionStorage.removeItem(AuthStorageKeys.externalAccessToken)
		sessionStorage.removeItem(InactivityStorageKeys.customTimeOut)

		//clear User Information
		clearUserInfo(this._urlConfig)

		//Remove event listeners
		window.removeEventListener(InactivityEvents.inactiveUser, this._boundInactiveUserHandler)
		window.removeEventListener('storage', this._boundStorageHandler)

		//Emit local storage event
		this._emitLocalStorageLogout()

		//If user has provided a url, redirect them to that url
		//If the url parameter is present and it is on the same domain as the current page, use the url parameter
		//Use case: if the url is relative or if the url is absolute but on the same page
		//Else, same domain is false
		const isSameDomain = url ? !isAbsoluteUrl(url) || new URL(url).origin === window.location.origin : false
		if (url && isSameDomain) {
			window.location.href = url
		} else {
			window.location.href = this._urlConfig.logoutURL
		}
	}

	/**
	 * Custom client application implementation of register function
	 * @param url - url to go to create an account
	 * @param stateURL - url to go to after creating an account
	 */
	registerRedirect(url?: string, stateURL?: string): void {
		if (url) {
			if (stateURL) {
				const stateURLQuery = '?state=' + stateURL
				const finalURL = new URL(url + stateURLQuery)
				window.location.href = finalURL.href
			} else {
				window.location.href = url
			}
		} else {
			window.location.reload()
		}
	}

	/**
	 * Returns idToken for custom mobile application if it exists, else returns null
	 */
	get idToken(): string | null {
		return this._idToken
	}

	get missingInfoRequired(): string | null {
		return requiredMIVal
	}

	get authState(): number {
		return this._authState
	}

	setInactiveTimeOut(timeout: number): void {
		const inactiveTimeout = timeout.toString()
		sessionStorage.setItem(InactivityStorageKeys.customTimeOut, inactiveTimeout)
		window.location.reload()
	}
}
